// src/hooks/use-attributes.ts

/**
 * TanStack Query hooks for Attribute Management
 * Provides attribute CRUD operations with caching and optimistic updates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchAttributes,
  fetchAttribute,
  createAttribute,
  updateAttribute,
  deleteAttribute,
} from '@/lib/attributes-api'
import { isApiError } from '@/lib/api-client'
import type {
  AttributeQueryParams,
  CreateAttributeRequest,
  UpdateAttributeRequest,
  AttributeCategory,
} from '@/types/attribute'

/**
 * Query keys for attribute management
 */
export const attributeKeys = {
  all: ['attributes'] as const,
  lists: () => [...attributeKeys.all, 'list'] as const,
  list: (params: AttributeQueryParams) => [...attributeKeys.lists(), params] as const,
  details: () => [...attributeKeys.all, 'detail'] as const,
  detail: (id: string) => [...attributeKeys.details(), id] as const,
}

/**
 * Fetch list of attributes with pagination and filters
 */
export function useAttributes(params: AttributeQueryParams = {}) {
  return useQuery({
    queryKey: attributeKeys.list(params),
    queryFn: () => fetchAttributes(params),
  })
}

/**
 * Fetch attributes by category (convenience hook)
 */
export function useAttributesByCategory(
  category: AttributeCategory,
  options: Omit<AttributeQueryParams, 'category'> = {}
) {
  return useAttributes({ ...options, category })
}

/**
 * Fetch all attributes across categories (for combobox usage)
 */
export function useAllAttributes() {
  return useAttributes({ pageSize: 500 })
}

/**
 * Fetch a single attribute by ID
 */
export function useAttribute(id: string) {
  return useQuery({
    queryKey: attributeKeys.detail(id),
    queryFn: () => fetchAttribute(id),
    enabled: !!id,
  })
}

/**
 * Create a new attribute
 */
export function useCreateAttribute() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAttributeRequest) => createAttribute(data),
    onSuccess: (newAttribute) => {
      // Invalidate all attribute lists to refetch
      queryClient.invalidateQueries({ queryKey: attributeKeys.lists() })
      toast.success(`Attribute "${newAttribute.displayName}" created successfully`)
    },
    onError: (error) => {
      if (isApiError(error)) {
        if (error.code === 'DUPLICATE_ATTRIBUTE') {
          toast.error('An attribute with this key already exists')
        } else {
          toast.error(error.message || 'Failed to create attribute')
        }
      } else {
        toast.error('Failed to create attribute')
      }
    },
  })
}

/**
 * Update an existing attribute
 */
export function useUpdateAttribute() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAttributeRequest }) =>
      updateAttribute(id, data),
    onSuccess: (updatedAttribute) => {
      // Update cache for this specific attribute
      queryClient.setQueryData(
        attributeKeys.detail(updatedAttribute.id),
        updatedAttribute
      )
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: attributeKeys.lists() })
      toast.success(`Attribute "${updatedAttribute.displayName}" updated successfully`)
    },
    onError: (error) => {
      if (isApiError(error)) {
        toast.error(error.message || 'Failed to update attribute')
      } else {
        toast.error('Failed to update attribute')
      }
    },
  })
}

/**
 * Delete an attribute
 */
export function useDeleteAttribute() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteAttribute(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: attributeKeys.detail(deletedId) })
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: attributeKeys.lists() })
    },
    onError: (error) => {
      if (isApiError(error)) {
        toast.error(error.message || 'Failed to delete attribute')
      } else {
        toast.error('Failed to delete attribute')
      }
    },
  })
}
